import { Component, OnInit } from '@angular/core';
import { ModalController } from "@ionic/angular/lazy";
import { FormBuilder, Validators } from "@angular/forms";
import { firstValueFrom } from "rxjs";
import { take } from "rxjs/operators";
import { UserService } from "../../services/user.service";
import { ToastService } from "../../services/toast.service";
import { LangService } from "../../services/lang.service";
import { minSelectedValidator } from "../../structure/validators/min-selected.validator";
import { isDomesticAbuseTag } from "../../structure/helpers/da-tag.helper";

type DetailsField = 'registration_type' | 'crime_types';

@Component({
    selector: 'app-complete-details-modal',
    templateUrl: './complete-details-modal.component.html',
    styleUrls: ['./complete-details-modal.component.scss'],
    standalone: false
})
export class CompleteDetailsModalComponent implements OnInit {

    detailsForm: any;
    tag: string | null = null;
    submitting = false;

    /** Flips the modal over to the confirmation card, and is what the callers of
     *  openCompleteDetailsModal() receive on dismiss. */
    saved = false;

    fields: DetailsField[] = [];

    constructor(
        public modalCtrl: ModalController,
        public formBuilder: FormBuilder,
        private userService: UserService,
        private toastService: ToastService,
        public lang: LangService,
    ) {}

    async ngOnInit() {
        // Read the account before building the form: only the parts the member is
        // actually missing get a field and a control, so the whole-form `invalid`
        // check in save() can never be blocked by a field they were never shown.
        const account: any = await firstValueFrom(this.userService.data.pipe(take(1)));

        this.tag = account?.tag ?? null;

        if (account?.needs_registration_type) {
            this.fields.push('registration_type');
        }
        if (account?.needs_crime_types) {
            this.fields.push('crime_types');
        }
        // A backend without the per-field flags: fall back to asking for both.
        if (!this.fields.length) {
            this.fields = ['registration_type', 'crime_types'];
        }

        const controls: any = {};

        if (this.fields.includes('registration_type')) {
            controls.registration_type = [account?.registration_type ?? '', [Validators.required]];
        }
        if (this.fields.includes('crime_types')) {
            controls.crime_type_ids = [account?.crime_type_ids ?? [], [minSelectedValidator(1)]];
        }

        this.detailsForm = this.formBuilder.group(controls);
    }

    get f() {
        return this.detailsForm.controls;
    }

    get heroTitle(): string {
        return this.saved
            ? (this.lang.data['account.complete_details.saved_title'] || 'Selection saved')
            : (this.lang.data['account.complete_details.modal_title'] || 'Just a few more details');
    }

    get showRegistrationType(): boolean {
        return this.fields.includes('registration_type');
    }

    get showCrimeTypes(): boolean {
        return this.fields.includes('crime_types');
    }

    get preselectDomesticAbuse(): boolean {
        return isDomesticAbuseTag(this.tag);
    }

    async save() {
        this.detailsForm.markAllAsTouched();

        if (this.detailsForm.invalid) {
            return;
        }

        // Send only the fields that were asked for — the endpoint leaves omitted
        // fields untouched.
        const params: any = {};

        if (this.showRegistrationType) {
            params.registration_type = this.f['registration_type'].value;
        }
        if (this.showCrimeTypes) {
            params.crime_type_ids = this.f['crime_type_ids'].value;
        }

        this.submitting = true;
        try {
            await this.toastService.showLoading();
            await this.userService.updateSupportDetails(params);
            // Refresh the account so needs_account_details flips to false and the
            // banner + virtual notification disappear.
            await this.userService.getAccount(true);
            await this.toastService.dismissLoading();
            this.saved = true;
        } catch (error: any) {
            await this.toastService.dismissLoading();
            const response = error?.response;
            (response?.hasOwnProperty('data'))
                ? this.toastService.toast(this.lang.data[response.data.message])
                : this.toastService.toastGeneralError();
        } finally {
            this.submitting = false;
        }
    }

    dismiss() {
        return this.modalCtrl.dismiss({ saved: this.saved }, this.saved ? 'saved' : 'cancel');
    }
}
